import { PRODUCTS } from "@/content/products";
import { ActionLink, Container, Eyebrow, Mono, Section, SectionTitle, TextLink } from "@/components/site/primitives";
import { Placeholder } from "@/components/site/Placeholder";
import { cn } from "@/lib/utils";

const { briefcase: B, forma: F, lumen: L } = PRODUCTS;

const BriefcaseBand = () => (
  <article className="t-briefcase grid grid-cols-1 bg-o-bg text-o-text md:grid-cols-12" data-testid="home-product-briefcase">
    <div className="flex flex-col justify-between gap-8 p-6 sm:p-8 md:col-span-5">
      <div>
        <Mono accent>Human activity · {B.activity}</Mono>
        <h3 className="mt-4 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Briefcase</h3>
        <p className="mt-4 text-o-dim">{B.tagline}</p>
      </div>
      <div>
        <ul className="flex flex-wrap gap-2">
          {B.movements.map((m) => (
            <li key={m.id} className="border border-o-line px-2.5 py-1 font-mono text-[10.5px] uppercase tracking-[0.14em] text-o-dim">{m.label}</li>
          ))}
        </ul>
        <TextLink to={B.path} testid="home-open-briefcase" className="mt-6">Open Briefcase</TextLink>
      </div>
    </div>
    <div className="p-3 sm:p-4 md:col-span-7">
      <Placeholder label={B.placeholder} note="Wide product imagery" ratio="16 / 9" className="h-full" />
    </div>
  </article>
);

const FormaPanel = ({ className }) => (
  <article className={cn("t-forma flex flex-col bg-o-bg p-6 text-o-text sm:p-8", className)} data-testid="home-product-forma">
    <Mono accent>Human activity · {F.activity}</Mono>
    <p className="mt-6 font-display text-3xl font-bold leading-[1.05] text-o-accent sm:text-4xl">{F.headline}</p>
    <h3 className="mt-6 font-display text-2xl font-extrabold">Forma</h3>
    <p className="mt-2 text-sm text-o-dim">{F.tagline}</p>
    <Placeholder label={F.placeholder} note="Portrait format" ratio="4 / 5" className="mt-6" />
    <TextLink to={F.path} testid="home-open-forma" className="mt-6">Open Forma</TextLink>
  </article>
);

const LumenPanel = ({ className }) => (
  <article className={cn("t-lumen flex flex-col bg-o-bg p-6 text-o-text sm:p-8", className)} data-testid="home-product-lumen">
    <Placeholder label={L.placeholder} note="Landscape, human setting of education" ratio="3 / 2" />
    <div className="mt-6 grid gap-6 sm:grid-cols-2">
      <div>
        <Mono accent>Human activity · {L.activity}</Mono>
        <h3 className="mt-4 font-display text-3xl font-extrabold">Lumen</h3>
        <p className="mt-2 text-sm text-o-dim">{L.tagline}</p>
      </div>
      <ol className="space-y-1.5 self-end">
        {[...L.scales].reverse().map((s, i) => (
          <li key={s.id} className="border border-o-line bg-o-surface px-3 py-2 text-sm" style={{ marginLeft: `${i * 12}px` }}>{s.label}</li>
        ))}
      </ol>
    </div>
    <TextLink to={L.path} testid="home-open-lumen" className="mt-6">Open Lumen</TextLink>
  </article>
);

export const ProductOrigination = () => (
  <Section theme="t-stone" testid="home-products" id="research-products">
    <Container className="py-24 lg:py-32">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow index="04">Research & Products · Company-originated</Eyebrow>
          <SectionTitle className="mt-6">Products that begin with a human activity.</SectionTitle>
        </div>
        <p className="leading-relaxed text-o-dim lg:col-span-5">
          Research & Products starts from independent research into human needs and possibilities, develops the mechanisms an activity requires, and originates products around them. The same methods produce very different things.
        </p>
      </div>
      <div className="mt-14 space-y-4">
        <BriefcaseBand />
        <div className="grid gap-4 lg:grid-cols-12">
          <FormaPanel className="lg:col-span-5" />
          <LumenPanel className="lg:col-span-7" />
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-6 border-t border-o-line pt-6 md:flex-row md:items-center md:justify-between">
        <p className="max-w-xl text-sm text-o-dim">
          <span className="font-semibold text-o-text">Physical Breakpoint</span> · a supporting example of a software mechanism becoming a tangible product experience.{" "}
          <TextLink to="/research#physical-breakpoint" testid="home-physical-breakpoint">See it in Research</TextLink>
        </p>
        <ActionLink to="/research" testid="home-explore-research" className="self-start">Explore Research & Products</ActionLink>
      </div>
    </Container>
  </Section>
);
